import { useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import {PageLink, PageTitle} from '../../../../_metronic/layout/core/PageData'
import {useContractCreateMutation} from '../Contract.api'
import {FormikProvider, useFormik} from 'formik'
import { preventEnterSubmit } from '../../../util/Functions'
import {CustomInputRenderer} from '../../../common/component/CustomInputRenderer'
import { array, object, string } from 'yup'
import { IFilterDto } from '../../../common/model/Core.model'
import { ClientListItem } from '../../client/components/ClientListItem'
import { TagInputSearchListControls } from '../../../common/component/TagInputSearchListControls'
import classNames from 'classnames'
import { IClientDto, IClientFilter } from '../../client/Client.model'
import { useClientSearchMutation } from '../../client/Client.api'
import 'flatpickr/dist/themes/light.css'
import { DateInputPicker } from '../../../common/component/DateInputPicker'
import { IContractCreateRequestDto } from '../Contract.model'
import { DOCUMENT_TYPES, FREQUENCIES, FREQUENCY, SERVICE_TYPES } from '../../../util/Constants'
import { SubSectionHeader } from '../../../common/component/SubSectionHeader'
import { FilePicker } from '../../../common/component/FilePicker'
import { IAttachment } from '../../media/Media.model'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { IContactDto } from '../../contact/Contact.model'
import { ContactEditor } from '../../contact/components/ContactEditor'
import FormValidator from '../../../util/FormValidator.util'

interface ICreateFormikSchema {
  clientId: string
  documentTypeCode: string
  documentType: string
  serviceTypeCode: string
  serviceType: string
  serviceFrequencyCode: string
  serviceFrequency: string
  serviceResponseTime: string
  serviceAgreement: string
  serviceLocation: string
  startDate: string
  endDate: string
  comments?: string
  contacts: IContactDto[]
  sellers: IContactDto[]
  attachments: IAttachment[]
}

const ContractNewPage = () => {
  const intl = useIntl()
  const formValidator = new FormValidator(intl)
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(window.location.search)
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.CONTRACTS'}),
      path: '/contracts',
      isSeparator: false,
      isActive: false,
    },
  ]

  const didFetchSearch = useRef(false)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const [validationSchema, setValidationSchema] = useState(
    object().shape({
      clientId: formValidator.validateStringStrict(),
      documentTypeCode: formValidator.validateStringStrict(),
      documentType: string().when('documentTypeCode', {
        is: (value) => value === 'OTHER',
        then: () => formValidator.validateStringStrict(),
      }),
      serviceTypeCode: formValidator.validateStringStrict(),
      serviceType: string().when('serviceTypeCode', {
        is: (value) => value === 'OTHER',
        then: () => formValidator.validateStringStrict(),
      }),
      serviceFrequencyCode: formValidator.validateStringStrict(),
      serviceFrequency: string().when('serviceFrequencyCode', {
        is: (value) => value === FREQUENCY.OTHER,
        then: () => formValidator.validateStringStrict(),
      }),
      serviceResponseTime: formValidator.validateString(),
      startDate: formValidator.validateStringStrict(),
      endDate: formValidator.validateStringStrict(),
      serviceAgreement: formValidator.validateString(2096),
      serviceLocation: formValidator.validateString(),
      comments: formValidator.validateString(2096),
      contacts: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      sellers: array().min(1, intl.formatMessage({id: 'INPUT.VALIDATION.REQUIRED_AT_LEAST'}, {min: 1})),
      attachments: array().of(
        object({
          comments: formValidator.validateString(),
        })
      ),
    })
  )
  const [initialValues, setInitialValues] = useState<ICreateFormikSchema>({
    clientId: '',
    documentTypeCode: '',
    documentType: '',
    serviceTypeCode: '',
    serviceType: '',
    serviceFrequencyCode: '',
    serviceFrequency: '',
    serviceResponseTime: '',
    serviceAgreement: '',
    serviceLocation: '',
    startDate: '',
    endDate: '',
    comments: '',
    contacts: [],
    sellers: [],
    attachments: [],
  })

  const clientFilterKeys = {
    defaultKey: IClientFilter.NAME,
    defaultKeyList: [
      IClientFilter.ID,
      IClientFilter.NAME,
      IClientFilter.SECTOR,
      IClientFilter.ADDRESS,
    ],
  }
  const [clientFilterSuggestions, setClientFilterSuggestions] = useState<string[]>(
    clientFilterKeys.defaultKeyList
  )
  const [clientFilters, setClientFilters] = useState<IFilterDto[]>([])
  const [isClientLoading, setIsClientLoading] = useState(false)
  const [client, setClient] = useState<IClientDto>()
  const [clientSearchResult, setClientSearchResult] = useState<IClientDto[] | undefined>(undefined)

  const [clientSearch] = useClientSearchMutation()
  const [contractCreate] = useContractCreateMutation()

  const [isAttachmentProcessing, setIsAttachmentProcessing] = useState(false)

  const formik = useFormik<ICreateFormikSchema>({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        if (values) {
          setSubmitting(true)

          const request: IContractCreateRequestDto = {
            clientId: values.clientId,
            documentTypeCode: values.documentTypeCode,
            documentType: intl.formatMessage({
              id: `DROPDOWN.DOCUMENT_TYPE.${values.documentTypeCode}`,
            }),
            serviceTypeCode: values.serviceTypeCode,
            serviceType: intl.formatMessage({
              id: `DROPDOWN.SERVICE_TYPE.${values.serviceTypeCode}`,
            }),
            serviceResponseTime: values.serviceResponseTime,
            serviceFrequencyCode: values.serviceFrequencyCode,
            serviceFrequency:
              values.serviceFrequencyCode === FREQUENCY.OTHER
                ? values.serviceFrequency.trim().toLocaleUpperCase()
                : intl.formatMessage({id: `DROPDOWN.FREQUENCY.${values.serviceFrequencyCode}`}),
            serviceAgreement: values.serviceAgreement,
            serviceLocation: values.serviceLocation,
            startDate: values.startDate,
            endDate: values.endDate,
            comments: values.comments,
            contacts: values.contacts,
            sellers: values.sellers,
            attachments: values.attachments,
          }

          const response: any = await contractCreate(request).unwrap()
          if (response && response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            navigate(`/contracts/${response.id}`)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        setClientSearchResult(undefined)
      }
    }
    window.addEventListener('keydown', handleEsc)

    return () => {
      window.removeEventListener('keydown', handleEsc)
    }
  }, [])

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    const fetchClient = async (clientId) => {
      try {
        if (!didFetchSearch.current) {
          setIsClientLoading(true)
          const defaultFilters = [{id: IClientFilter.HASH, value: clientId, editable: true}]
          const response: any = await clientSearch({filters: defaultFilters})
          if (!response || response.error) {
            setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
          } else {
            const client: IClientDto = response.data.data[0]
            setClient(client)

            formik.setFieldValue('clientId', client.id)
            formik.setFieldValue('contacts', [
              ...client.contacts,
              ...formik.values.contacts,
            ])

            setClientFilters(response.data.filters)
          }
        }
      } catch (e: any) {
        setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
      } finally {
        setIsClientLoading(false)
      }
    }

    const clientId = queryParams.get('clientId')
    if (clientId) {
      fetchClient(clientId)
    }
  }, [])

  const clientRowOnClickHandler = async (clientId) => {
    if (clientSearchResult) {
      const client = clientSearchResult.find((client) => client.id === clientId)
      if (client) {
        setClient(client)
        setClientSearchResult(undefined)
        setClientFilters([])
        setClientFilterSuggestions(clientFilterKeys.defaultKeyList)

        formik.setFieldValue('clientId', client.id)
        formik.setFieldValue('contacts', [
          ...client.contacts,
          ...formik.values.contacts,
        ])
      }
    }
  }

  const clientRemoveOnClickHandler = async () => {
    setClient(undefined)
    setClientSearchResult(undefined)
    setClientFilters([])
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)

    formik.setFieldValue('clientId', '')
    formik.setFieldValue('contacts', [
      // ...formik.values.contacts.filter((contact) => !contact.primary),
    ])
  }

  const clientSearchOnClickHandler = async () => {
    try {
      if (!didFetchSearch.current) {
        setIsClientLoading(true)
        const response: any = await clientSearch({
          filters: clientFilters,
        })
        if (!response || response.error) {
          setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
        } else {
          setClientSearchResult(response.data.data)
          setClientFilters(response.data.filters)
        }
      }
    } catch (e: any) {
      setToastEntry([MessageType.ERROR, intl.formatMessage({id: 'ERROR.MESSAGE.UNKNOWN'})])
    } finally {
      setIsClientLoading(false)
    }
  }

  const clientResetSearchResetOnClickHandler = async () => {
    setClientSearchResult(undefined)
    setClientFilterSuggestions(clientFilterKeys.defaultKeyList)
  }

  const tabWithError = (tabId) => {
    switch (tabId) {
      case 'kt_tab_pane_1': {
        return (
          (formik.touched.clientId && formik.errors.clientId) ||
          (formik.touched.documentTypeCode && formik.errors.documentTypeCode) ||
          (formik.touched.serviceTypeCode && formik.errors.serviceTypeCode) ||
          (formik.touched.serviceFrequencyCode && formik.errors.serviceFrequencyCode) ||
          (formik.touched.serviceFrequency && formik.errors.serviceFrequency) ||
          (formik.touched.startDate && formik.errors.startDate) ||
          (formik.touched.endDate && formik.errors.endDate) ||
          (formik.touched.serviceResponseTime && formik.errors.serviceResponseTime) ||
          (formik.touched.serviceAgreement && formik.errors.serviceAgreement) ||
          (formik.touched.comments && formik.errors.comments)
        )
      }
      case 'kt_tab_pane_2': {
        return formik.touched.contacts && formik.errors.contacts
      }
      case 'kt_tab_pane_3': {
        return formik.touched.sellers && formik.errors.sellers
      }
      case 'kt_tab_pane_4': {
        return formik.touched.attachments && formik.errors.attachments
      }
      default:
        return false
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='card card-flush card-stretch-100 card-bordered mt-5'>
        <div className='card-body'>
          {loadError && (
            <div className='alert alert-danger d-flex align-items-center px-5 py-2 mb-10'>
              <span className='svg-icon svg-icon-2hx svg-icon-primary ms-3 me-5'>
                <i className='bi bi-database-exclamation fs-2'></i>
              </span>

              <div className='d-flex flex-column'>
                <span>{loadError}</span>
              </div>

              <button
                type='button'
                className='position-absolute position-sm-relative m-2 m-sm-0 top-0 end-0 btn btn-icon ms-sm-auto'
                data-bs-dismiss='alert'
              >
                <div className='badge px-3 py-2'>
                  <span className='text-danger fs-1'>&times;</span>
                </div>
              </button>
            </div>
          )}

          <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit} onKeyDown={preventEnterSubmit} noValidate>
              <div className='d-flex flex-row'>
                <div className='d-flex flex-column flex-row-fluid'>
                  <div className='d-flex flex-row flex-column-fluid'>
                    <div className='d-flex flex-row-fluid flex-start'>
                      <div className='d-flex flex-column'>
                        <span className='text-gray-800 fs-2 fw-bolder'>
                          {intl.formatMessage({id: 'UTIL.CREATE.CONTRACT'})}
                        </span>
                      </div>
                    </div>

                    <div className='d-flex flex-row-auto flex-start ms-5'>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary me-2 d-none d-md-block'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            {intl.formatMessage({id: 'UTIL.PROCESSING'})}
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <>
                            <i className='bi bi-floppy'></i>
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'UTIL.SAVE'})}
                            </span>
                          </>
                        )}
                      </button>
                      <button
                        type='submit'
                        className='btn btn-sm btn-primary btn-active-color-secondary me-2 d-md-none'
                        onClick={() => formik.handleSubmit()}
                        disabled={isAttachmentProcessing || formik.isSubmitting}
                      >
                        {formik.isSubmitting ? (
                          <span className='indicator-progress' style={{display: 'block'}}>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                          </span>
                        ) : (
                          <i className='bi bi-floppy'></i>
                        )}
                      </button>

                      {!(isAttachmentProcessing || formik.isSubmitting) && (
                        <button
                          className='btn btn-sm btn-light'
                          onClick={() => navigate(`/contracts`)}
                        >
                          <i className='bi bi-x-lg'></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6 mt-5'>
                  <li className='nav-item'>
                    <a className='nav-link active' data-bs-toggle='tab' href='#kt_tab_pane_1'>
                      {tabWithError('kt_tab_pane_1') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.INFORMATION'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_2'>
                      {tabWithError('kt_tab_pane_2') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.CONTACTS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_3'>
                      {tabWithError('kt_tab_pane_3') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.SELLERS'})}
                    </a>
                  </li>
                  <li className='nav-item'>
                    <a className='nav-link' data-bs-toggle='tab' href='#kt_tab_pane_4'>
                      {tabWithError('kt_tab_pane_4') && <span className='text-danger me-1'>*</span>}
                      {intl.formatMessage({id: 'UTIL.ATTACHMENTS'})}
                    </a>
                  </li>
                </ul>
                <div className='tab-content mt-10' id='myTabContent'>
                  <div className='tab-pane fade active show' id='kt_tab_pane_1' role='tabpanel'>
                    <div className='row mb-5'>
                      {!client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span
                              className={classNames('required', {
                                'text-gray-700': !(
                                  formik?.touched?.clientId && formik?.errors?.clientId
                                ),
                                'text-danger':
                                  formik?.touched?.clientId && formik?.errors?.clientId,
                              })}
                            >
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <TagInputSearchListControls
                              loading={isClientLoading}
                              enabled={!formik.isSubmitting}
                              filterKeys={clientFilterKeys}
                              filterSuggestions={clientFilterSuggestions}
                              filters={clientFilters}
                              createOnClickPath={'/clients/new'}
                              setFilterSuggestions={setClientFilterSuggestions}
                              setFilters={setClientFilters}
                              searchOnClickHandler={clientSearchOnClickHandler}
                              resetSearchResetOnClickHandler={clientResetSearchResetOnClickHandler}
                            ></TagInputSearchListControls>
                          </div>
                          <label className='col-xxl-3'></label>
                          <div className='col-xxl-9 h-3px'>
                            {clientSearchResult && clientSearchResult?.length > 0 && (
                              <ul
                                id='report-client-autocomplete-list'
                                className='suggestions-list'
                                role='listbox'
                              >
                                {clientSearchResult.map((item, index) => (
                                  <li key={index} onClick={() => clientRowOnClickHandler(item.id)}>
                                    <ClientListItem data={item}></ClientListItem>
                                    <div className='separator'></div>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </>
                      )}

                      {client && (
                        <>
                          <label className='col-xxl-3 fs-6 col-form-label col-form-label-sm'>
                            <span className='text-gray-700 required'>
                              {intl.formatMessage({id: 'UTIL.CLIENT'})}
                            </span>
                          </label>

                          <div className='col-xxl-9'>
                            <ClientListItem
                              disabled={formik.isSubmitting}
                              data={client}
                              rowSelected={client.id}
                              removeOnClick={clientRemoveOnClickHandler}
                            ></ClientListItem>
                          </div>
                        </>
                      )}
                    </div>
                    <div className='row mb-5'>
                      <SubSectionHeader
                        separator={false}
                        title={intl.formatMessage({id: 'UTIL.GENERAL'})}
                      />

                      <div className='px-5'>
                        <div className='row mb-5'>
                          <DateInputPicker
                            label={intl.formatMessage({id: 'UTIL.DATE_START'})}
                            placeholder={intl.formatMessage({id: 'UTIL.DATE_START'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='startDate'
                          ></DateInputPicker>

                          <DateInputPicker
                            label={intl.formatMessage({id: 'UTIL.DATE_END'})}
                            placeholder={intl.formatMessage({id: 'UTIL.DATE_END'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='endDate'
                          ></DateInputPicker>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.DOCUMENT_TYPE'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='documentTypeCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('documentTypeCode')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {DOCUMENT_TYPES &&
                                  DOCUMENT_TYPES.map((documentType, index) => (
                                    <option key={index} value={documentType}>
                                      {intl.formatMessage({
                                        id: `DROPDOWN.DOCUMENT_TYPE.${documentType}`,
                                      })}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_TYPE'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='serviceTypeCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('serviceTypeCode')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {SERVICE_TYPES &&
                                  SERVICE_TYPES.map((serviceType, index) => (
                                    <option key={index} value={serviceType}>
                                      {intl.formatMessage({
                                        id: `DROPDOWN.SERVICE_TYPE.${serviceType}`,
                                      })}
                                    </option>
                                  ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_FREQUENCY'})}
                            required={true}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='serviceFrequencyCode'
                            object={
                              <select
                                className='form-select form-select-solid form-select-sm px-5 fs-6 text-dark'
                                {...formik.getFieldProps('serviceFrequencyCode')}
                                onChange={(e) => {
                                  if (e.target.value === FREQUENCY.OTHER) {
                                    formik.setFieldValue('serviceFrequency', '')
                                  }
                                  formik.handleChange(e)
                                }}
                                disabled={formik.isSubmitting}
                              >
                                <option value=''>
                                  {intl.formatMessage({id: 'UTIL.DROPDOWN.DEFAULT'})}
                                </option>
                                {FREQUENCIES.map((frequency, index) => (
                                  <option key={index} value={frequency}>
                                    {intl.formatMessage({id: `DROPDOWN.FREQUENCY.${frequency}`})}
                                  </option>
                                ))}
                              </select>
                            }
                          ></CustomInputRenderer>

                          {formik.values.serviceFrequencyCode === FREQUENCY.OTHER && (
                            <CustomInputRenderer
                              placeholder={intl.formatMessage({id: 'UTIL.SPECIFY'})}
                              disabled={formik.isSubmitting}
                              formik={formik}
                              fieldName={`serviceFrequency`}
                              enableInputLength={true}
                            ></CustomInputRenderer>
                          )}

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_LOCATION'})}
                            placeholder={intl.formatMessage({id: 'UTIL.SERVICE_LOCATION'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName={`serviceLocation`}
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.RESPONSE_TIME'})}
                            placeholder={intl.formatMessage({id: 'UTIL.RESPONSE_TIME'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='serviceResponseTime'
                            enableInputLength={true}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.SERVICE_AGREEMENT'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='serviceAgreement'
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                placeholder={intl.formatMessage({id: 'UTIL.SERVICE_AGREEMENT'})}
                                {...formik.getFieldProps('serviceAgreement')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              />
                            }
                            enableInputLength={true}
                            inputLength={3000}
                          ></CustomInputRenderer>

                          <CustomInputRenderer
                            label={intl.formatMessage({id: 'UTIL.COMMENTS'})}
                            disabled={formik.isSubmitting}
                            formik={formik}
                            fieldName='comments'
                            object={
                              <textarea
                                rows={4}
                                className='form-control form-control-lg form-control-solid px-5 py-3 fs-6 text-dark'
                                placeholder={intl.formatMessage({id: 'UTIL.COMMENTS'})}
                                {...formik.getFieldProps('comments')}
                                onChange={(e) => formik.handleChange(e)}
                                disabled={formik.isSubmitting}
                              />
                            }
                            enableInputLength={true}
                            inputLength={3000}
                          ></CustomInputRenderer>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_2' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <div className='row mb-5'>
                          <ContactEditor
                            formik={formik}
                            fieldName='contacts'
                            disabled={formik.isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_3' role='tabpanel'>
                    <div className='row mb-5'>
                      <div className='px-5'>
                        <div className='row mb-5'>
                          <ContactEditor
                            formik={formik}
                            fieldName='sellers'
                            disabled={formik.isSubmitting}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='tab-pane fade' id='kt_tab_pane_4' role='tabpanel'>
                    <div className='row mb-5'>
                      <FilePicker
                        disabled={formik.isSubmitting}
                        formik={formik}
                        fieldName='attachments'
                        setIsAttachmentProcessing={setIsAttachmentProcessing}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </FormikProvider>
        </div>
      </div>
    </>
  )
}

export {ContractNewPage}
